import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { productsService, ProductFilters, ProductCreate, PriceUpdateBulk } from '@/services/products';

export function useProducts(filters?: ProductFilters, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['products', filters],
    queryFn: () => productsService.getProducts(filters),
    enabled: options?.enabled ?? true,
  });
}

export function useProduct(id: number) {
  return useQuery({
    queryKey: ['products', id],
    queryFn: () => productsService.getProduct(id),
    enabled: !!id,
  });
}

export function useCategories() {
  return useQuery({
    queryKey: ['categories'],
    queryFn: productsService.getCategories,
  });
}

export function useCreateProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ProductCreate) => productsService.createProduct(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useUpdateProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
      options,
    }: {
      id: number;
      data: Partial<ProductCreate>;
      options?: {
        cost_notes?: string;
        supplier?: string;
        quantity_purchased?: number;
      };
    }) => productsService.updateProduct(id, data, options),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['cost-history'] });
    },
  });
}

export function useUpdateStock() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, quantity }: { id: number; quantity: number }) =>
      productsService.updateStock(id, quantity),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
  });
}

export function useBulkUpdatePrices() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: PriceUpdateBulk) => productsService.bulkUpdatePrices(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useBulkUpdatePricesByIds() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      product_ids: number[];
      percentage: number;
      recalculate_from_cost?: boolean;
      reason?: string;
    }) => productsService.bulkUpdatePricesByIds(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['cost-history'] });
    },
  });
}

export function useRecalculateListPrices() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (categoryId: number | undefined = undefined) => productsService.recalculateListPrices(categoryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useCreateCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; description?: string }) =>
      productsService.createCategory(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    },
  });
}

export function useUpdateCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: {
      id: number;
      data: {
        name?: string;
        description?: string;
        is_active?: boolean;
        price_formula?: string;
        price_multiplier?: number;
        margin_percentage?: number;
        price_round_to?: number;
        list_price_multiplier?: number;
      }
    }) => productsService.updateCategory(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useDeleteCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => productsService.deleteCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    },
  });
}

export function useDeleteProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => productsService.deleteProduct(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}
